/* eslint-disable @typescript-eslint/member-ordering */
import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule, formatDate } from '@angular/common';
import {
    Component,
    computed,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    Renderer2,
    signal,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {
    AbstractControl,
    FormsModule,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    Validators
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ErrorStateMatcher, MatNativeDateModule } from '@angular/material/core';
import { MatCalendar, MatCalendarCellCssClasses, MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatLegacyMenuTrigger as MatMenuTrigger } from '@angular/material/legacy-menu';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TimePickerModule } from '@progress/kendo-angular-dateinputs';
import dayjs from 'dayjs';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { AccountManagersService } from 'src/app/accountmanagers/accountmanagers.service';
import { HospTypeEnum } from 'src/app/compliance/api/enums/hosp-type-enum.enum';
import { WINDOW } from 'src/app/core/core.module';
import { IdentityService } from 'src/app/core/identity.service';
import { ContractMinDaysStatus } from 'src/app/margin-calculator/models/contract-min-days-status.model';
import { MarginCalculatorMarginService } from 'src/app/margin-calculator/services/margin-calculator-margin.service';
import { MarginCalculatorStateHelpers } from 'src/app/margin-calculator/store/state/margin-calculator-state-helpers';
import { DirectOfferCreateMarginDialogComponent } from 'src/app/recruiting/direct-offer-margin/direct-offer-create-margin-dialog.component';
import { DirectOfferImportMarginDialogComponent } from 'src/app/recruiting/direct-offer-margin/direct-offer-import-margin-dialog.component';
import { DirectOfferMarginService } from 'src/app/recruiting/direct-offer-margin/direct-offer-margin.service';
import { offerRescindedSubStatuses } from 'src/app/root-store/account-managers-store/account-managers.selectors';
import { getSubmittalStatuses } from 'src/app/root-store/account-managers-store/actions/account-managers.actions';
import { CandidateOfferNoteCategory } from 'src/app/shared/models/enums/candidate-offer-note-category.enum';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { OfferStatus, OffersUserType } from 'src/app/shared/models/enums/offers.enum';
import { CodeBustersFeatureFlags } from 'src/app/shared/models/enums/team-feature-flags/code-busters-feature-flags.enum';
import { ListItem } from 'src/app/shared/models/list-item.model';
import {
    Attachment,
    Candidates,
    DeclineReasons,
    DetailLookups,
    Expertise,
    Facilities,
    Facility,
    FacilityLookups,
    Offer,
    OfferDetails,
    OfferJobDetails,
    OfferNotes,
    OfferRates,
    OfferType,
    ProfessionTypes,
    Professions,
    RTOApprovalStatuses,
    SubmittalSnapshotData,
    VendorLookups,
    VendorOptions,
    OfferNoteAndCategory,
    CandidateOfferChangeData
} from 'src/app/shared/models/offers.model';
import { DialogSharedDeclineOfferComponent } from 'src/app/shared/offers-shared/decline-dialog/decline-dialog.component';
import { DialogContractRequestedOfferComponent } from 'src/app/shared/offers-shared/offer-details/dialog-contract-requested-offer/dialog-contract-requested-offer.component';
import { OfferMarginDifference } from 'src/app/shared/offers-shared/offer-details/dialog-offer-margin-differences/model/offer-margin-difference.model';
import { OfferMarginDifferencesDialogComponent } from 'src/app/shared/offers-shared/offer-details/dialog-offer-margin-differences/offer-margin-differences-dialog.component';
import { OfferReasonDialogComponent } from 'src/app/shared/offers-shared/offer-dialogs/offer-reason-dialog/offer-reason-dialog.component';
import { SharedOffersService } from 'src/app/shared/offers-shared/offers.service';
import { ConfirmDialogService } from 'src/app/shared/services/confirm-dialog.service';
import { SharedOffersService as OffersHelper } from 'src/app/shared/services/offers.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { isEmpty } from 'src/app/shared/utilities/obj-utilities';
import { BannerComponent } from '../../banner.component';
import { RecruiterBonusComponent } from '../../components/recruiter-bonus/recruiter-bonus.component';
import { WfdPriorityReturnerChipsViewComponent } from '../../components/wfd-priority-returner-chips/priority-returner-view/priority-returner-view.component';
import { DetailToolbarComponent } from '../../detail-toolbar.component';
import { RtoDatesHelpers } from '../../helpers';
import { EmploymentTypeCode } from '../../models/enums/employment-type-code.enum';
import { OfferReasonResult } from '../../models/offer-reason-result.model';
import { SubmittalSubStatus } from '../../models/submittal-status.model';
import { ArrayContainsPipe } from '../../pipes/array-contains.pipe';
import { RtoAlertIconComponent } from '../../rto-alerts/rto-alert-icon/rto-alert-icon';
import { RtoWarningMessageComponent } from '../../rto-alerts/rto-warning-message/rto-warning-message';
import { LDFeatureManager } from '../../services/ld-feature-manager';
import { UnitDescriptionSecureWindowService } from '../../services/unit-description-secure-window.service';
import { WfdCandidateFacilityService } from '../../services/wfd-candidate-facility.service';
import { DeclinePanelComponent } from '../decline-panel/decline-panel.component';
import { SharedNotesComponent } from '../notes-display/notes-display.component';
import { SharedOfferAttachmentsComponent } from '../offer-attachments/offer-attachments.component';
import { DialogRequestedChangesComponent } from './dialog-request-changes/dialog-request-changes.component';
import { SharedOfferDetailsViewComponent } from 'src/app/shared/offers-shared/offer-details-view/offer-details-view.component';
import { PointsOfContactComponent } from 'src/app/recruiting/deals/deal-details/points-of-contact/points-of-contact.component';
import {
    areRtoListsEqual,
    buildSnapshotDiffs,
    isOfferSnapshotDifferent,
    normalizeDayControl,
    normalizeNumberControl,
    normalizeTimeControl,
    SnapshotDiffKey,
    SnapshotDiffKeyLabel,
    SnapshotDiffMap
} from 'src/app/shared/helpers/offer-snapshot-diff-helper';

@UntilDestroy()
@Component({
    selector: 'nova-shared-offer-details',
    templateUrl: './offer-details.component.html',
    styleUrls: ['./offer-details.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        MatSelectModule,
        MatCheckboxModule,
        MatRadioModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatCardModule,
        MatMenuModule,
        TextFieldModule,
        SharedNotesComponent,
        RtoWarningMessageComponent,
        DetailToolbarComponent,
        ArrayContainsPipe,
        BannerComponent,
        RtoAlertIconComponent,
        SharedOfferAttachmentsComponent,
        RecruiterBonusComponent,
        TimePickerModule,
        WfdPriorityReturnerChipsViewComponent,
        SharedOfferDetailsViewComponent,
        PointsOfContactComponent
    ]
})
export class SharedOfferDetailsComponent implements OnInit, OnChanges {
    readonly featureFlag = FeatureFlag;
    offerData: Offer;
    _offerDetails: OfferDetails;
    _updatedDetails: OfferJobDetails;
    lookups: DetailLookups;
    _expertise: Expertise[];
    _offerRates: OfferRates;
    _vendorRate: number;
    facilityLookup: FacilityLookups;
    rateEnabled = false;
    accountManagerName: string;
    attachmentData: Attachment[];
    offerId: number;
    marginId: number;
    vendorLookups: VendorLookups;
    initialFacilityLookup = true;
    constDateSelected = 'date-selected';
    constDateContractStartEndClass = 'date-contract-start-or-end';
    title: string;
    offerTypeEnum = OfferType;
    offerStatus = OfferStatus;
    offersUserTypeEnum = OffersUserType;
    offerNotesCategory = CandidateOfferNoteCategory;
    offerRescindedSubStauses$: Observable<SubmittalSubStatus[]>;
    isRecruiterSpecificNoteFlagEnabled = false;
    isOperationProcessing: boolean = false;
    readonly DATE_FORMAT_STRING = 'MM/DD/YYYY';

    isPanelOpen = false;

    isWfdReturner$: Observable<boolean>;
    wfdPriority$: Observable<number>;
    hospTypeEnum = HospTypeEnum;

    initialOfferValues: SubmittalSnapshotData = null;
    snapshotData = signal<SubmittalSnapshotData>(null);

    @ViewChild('clearCalendarTrigger') clearCalendarTrigger: MatMenuTrigger;
    @ViewChild('calendar', { static: false }) calendar: MatCalendar<Date>;

    @Input() isLoading: boolean;
    @Input() submittalId: number;

    @Input() startDate: Date;
    @Input() endDate: Date;
    @Input() userType: OffersUserType;
    @Input() attachmentsHeadingTitle: string;
    @Input() attachmentsHeadingTitleSubtext: string;
    @Input() declineReasons: DeclineReasons[];
    @Input() vendorOptions: VendorOptions[];
    @Input() facilityOptions: ListItem[];

    @Input() notesHeadingTitle = 'Notes/History';
    @Input() notesHeadingTitleSubtext = '';
    @Input() canModify: boolean;
    @Input() disableShowButtons = false;
    @Input() isDealDetailsLayout = false;

    @Input()
    set contractLength(contractLength: number) {
        if (contractLength) {
            this.displayedContractLengthWeeks = contractLength;
            this.detailsFormGroup.get('contractLength').patchValue(contractLength);
        }
    }

    @Input()
    set contractMinDaysStatus(contractMinDaysStatus: ContractMinDaysStatus) {
        if (contractMinDaysStatus) {
            this.detailsFormGroup.get('contractLengthDays').patchValue(contractMinDaysStatus.contractDays);
            this.displayedContractLengthDays = contractMinDaysStatus.contractDays;
            this.contractLengthDaysApprovalRequired = false;

            if (contractMinDaysStatus.approvalRequired) {
                this.contractLengthDaysInfoMessage = `Contract does not meet the ${contractMinDaysStatus.contractMinDays}
                day minimum for the state of ${contractMinDaysStatus.stateAbbreviation}.
                The Base Bill Rate for the contract length will need approval after submittal.`;
                this.contractLengthDaysApprovalRequired = true;
            }
        }
    }

    @Input()
    set offer(offer: Offer) {
        if (offer) {
            this.offerData = offer;
            this.offerId = offer.id;
            this.marginId = offer.marginId;
            this.title = ` Offer Details - ${this.offerData?.candidateName}`;
            if (offer.offerType === OfferType.Agency) {
                this.setAgencyOfferFieldsAndValidations();
            } else {
                this.setDirectOfferFieldsAndValidattions();
            }
            this.detailsFormGroup?.patchValue({
                professionId: this.offerData.jobProfessionId,
                candidateProfessionId: this.offerData.professionId
            });

            this.jobProfessionName = this.offerData.jobProfessionName;

            this.initContractLengthDaysAndWeeks(offer);

            this.setContractDateFieldsValidations();
        }
    }

    get offer(): Offer {
        return this.offerData;
    }

    @Input()
    set offerDetails(offerDetails: OfferDetails) {
        this._offerDetails = offerDetails;
        if (this._offerDetails) {
            this.patchEditValues();
            if (!offerDetails.facilityId) {
                this.detailsFormGroup.controls.candidateProfessionId.disable();
                this.detailsFormGroup.controls.expertiseId.disable();
            }
        }

        if (this.isOfferOptimizationsEnabled() && this.editMode) {
            this.setOfferValuesOnLoad();
        }
    }

    get offerDetails(): OfferDetails {
        return this._offerDetails;
    }

    @Input()
    offerMarginDifferences: OfferMarginDifference[];

    @Input()
    set updatedJobIdDetails(updatedDetails: OfferJobDetails) {
        if (updatedDetails) {
            this._updatedDetails = updatedDetails;
            const timeStart = dayjs().toDate();
            const timeAltStart = dayjs().toDate();
            const timeEnd = dayjs().toDate();
            const timeAltEnd = dayjs().toDate();

            timeStart.setHours(updatedDetails.shiftH);
            timeStart.setMinutes(updatedDetails.shiftM);

            timeEnd.setHours(updatedDetails.shiftToH);
            timeEnd.setMinutes(updatedDetails.shiftToM);

            timeAltStart.setHours(updatedDetails.shiftAltH);
            timeAltStart.setMinutes(updatedDetails.shiftAltM);

            timeAltEnd.setHours(updatedDetails.shiftAltToH);
            timeAltEnd.setMinutes(updatedDetails.shiftAltToM);

            this.professionSelection(updatedDetails.professionId);
            this.minEndDate = new Date(updatedDetails.startDate);

            this.detailsFormGroup.patchValue({
                ...updatedDetails,
                shiftTimeStart: timeStart,
                shiftTimeEnd: timeEnd,
                shiftAltTimeStart: timeAltStart,
                shiftAltTimeEnd: timeAltEnd,
                startDate: new Date(updatedDetails.startDate),
                endDate: new Date(updatedDetails.endDate)
            });
            if (!updatedDetails.facilityId) {
                this.detailsFormGroup.controls.candidateProfessionId.disable();
                this.detailsFormGroup.controls.expertiseId.disable();
            }

            const professionLookup = this.facilityLookups?.professions.find(
                (p) => p.id === updatedDetails.professionId
            );
            if (professionLookup) {
                this.detailsFormGroup.get('professionId').patchValue(professionLookup.id);
                this.jobProfessionName = professionLookup.name;
            } else {
                this.detailsFormGroup.get('professionId').setValue('');
                this.jobProfessionName = null;
            }
        }
    }

    get updatedJobIdDetails(): OfferJobDetails {
        return this._updatedDetails;
    }

    @Input()
    set detailLookups(detailLookups: DetailLookups) {
        if (detailLookups) {
            this.professionTypes = detailLookups.professionTypes;
            this.lookups = detailLookups;
            if (this.offerData?.offerType === OfferType.Direct) {
                this.lookups.contracts = this.lookups.contracts.filter((x) => x.code !== 'COC');
            }
            this.facilities = detailLookups.facilities;
            const id = this.detailsFormGroup.get('facilityId').value;
            const findFacility = this.facilities.find((obj) => obj.id === id);
            this.detailsFormGroup.get('facilityValues').patchValue(findFacility);
            if (!findFacility) {
                this.detailsFormGroup.get('facilityValues').reset();
            }
        }
    }

    get detailLookups(): DetailLookups {
        return this.lookups;
    }

    @Input()
    set facilityLookups(facilityLookups: FacilityLookups) {
        if (facilityLookups) {
            this.facilityLookup = facilityLookups;
            if (this.initialFacilityLookup) {
                this.initialFacilityLookup = false;
            } else {
                this.detailsFormGroup
                    .get('otherDetails')
                    .patchValue(this.offer.otherDetails ?? this.facilityLookup.otherDetails);
            }
            if (facilityLookups.jobs) {
                this.detailsFormGroup.get('jobId').enable();
                this.filteredJobIdOptions = this.detailsFormGroup.get('jobId').valueChanges.pipe(
                    startWith(''),
                    map((val) => this.filterJobIds(val))
                );
            }
            const candidateProfessionId = this.detailsFormGroup.get('candidateProfessionId').value;
            if (!candidateProfessionId) {
                this.detailsFormGroup.patchValue({
                    expertiseId: null
                });
            }
        }
    }

    get facilityLookups(): FacilityLookups {
        return this.facilityLookup;
    }

    @Input()
    set expertise(expertise: Expertise[]) {
        this._expertise = expertise;
    }

    get expertise(): Expertise[] {
        return this._expertise;
    }

    @Input()
    set updatedRates(rates: OfferRates) {
        if (rates) {
            this._offerRates = rates;
            this.detailsFormGroup.patchValue({ facilityRate: rates.facilityRate });
            this.detailsFormGroup.patchValue({ vendorRate: rates.vendorRate });
        }
    }

    get updatedRates(): OfferRates {
        return this._offerRates;
    }

    @Input()
    set updatedVendorRate(vendorRate: number) {
        if (vendorRate !== undefined) {
            this._vendorRate = vendorRate;
            this.detailsFormGroup.patchValue({ vendorRate });
        }
    }

    get updatedVendorRate(): number {
        return this._vendorRate;
    }

    @Input() offerNotes: OfferNotes[];

    @Input() editMode: boolean;

    @Input()
    set attachments(data: any) {
        if (data) {
            this.attachmentData = data;
        }
    }

    get attachments(): any {
        return this.attachmentData;
    }

    @Input()
    set vendorContacts(lookups: VendorLookups) {
        if (lookups) {
            this.vendorLookups = lookups;
            this.candidates = lookups.candidates;
            const activeVendorContact = this.vendorLookups.contacts.find(
                (vl) => vl.id === this.detailsFormGroup.get('vendorContactId').value
            );
            const candidateId = this.detailsFormGroup.get('candidateId').value;
            const existingCandidate = this.candidates
                ? this.candidates.find((item) => item.userId === candidateId)
                : null;
            this.detailsFormGroup.get('selectedCandidate').patchValue(existingCandidate);
            if (!activeVendorContact) {
                this.detailsFormGroup.patchValue({ vendorContactId: '' });
            }
        }
    }

    get vendorContacts(): VendorLookups {
        return this.vendorLookups;
    }

    getRtoApprovalStatus(): string {
        let status: string;
        switch (this.offerData?.rtoApprovalStatus) {
            case RTOApprovalStatuses.Approved: {
                status = 'Approved';
                break;
            }
            case RTOApprovalStatuses.PendingApproval: {
                status = 'Pending Approval';
                break;
            }
            default: {
                status = 'Not Approved';
                break;
            }
        }
        return status;
    }

    @Output() goBackClicked = new EventEmitter();
    @Output() addNoteEmit = new EventEmitter();
    @Output() enableEdit = new EventEmitter();
    @Output() enableView = new EventEmitter();
    @Output() facilitySelectionChange = new EventEmitter();
    @Output() professionSelectionChange = new EventEmitter();
    @Output() updateRatesEmit = new EventEmitter();
    @Output() updateVendorRateEmit = new EventEmitter();
    @Output() updateShowAPN = new EventEmitter();
    @Output() updateAPNReview = new EventEmitter();
    @Output() updateShowVendor = new EventEmitter();
    @Output() updateContractRequestedOffer = new EventEmitter();
    @Output() updateOfferEmit = new EventEmitter();
    @Output() jobIdSelectionChange = new EventEmitter();
    @Output() attachmentUpload = new EventEmitter();
    @Output() attachmentUpdate = new EventEmitter();
    @Output() attachmentDelete = new EventEmitter();
    @Output() attachmentGetFile = new EventEmitter();
    @Output() rescind = new EventEmitter();
    @Output() facilityRateCheck = new EventEmitter();
    @Output() calculateContractDetails = new EventEmitter();
    @Output() jobIdClick = new EventEmitter();
    @Output() declineOfferClick = new EventEmitter();
    @Output() doVendorSearch = new EventEmitter();
    @Output() doFacilitySearch = new EventEmitter();
    @Output() declineOfferEmit = new EventEmitter();
    @Output() openFacilityLink = new EventEmitter<number>();
    @Output() showToRecruiter = new EventEmitter();
    @Output() offerVerballyAccepted = new EventEmitter<number>();

    id: number;
    detailsFormGroup: UntypedFormGroup;
    lookups$: Observable<DetailLookups>;
    professions: Professions[] = [];
    facilities: Facilities[] = [];
    candidates: Candidates[] = [];
    professionTypes: ProfessionTypes[] = [];
    positionTypes: ProfessionTypes[] = [];
    lookupJobIds: number[] = [];
    filteredJobIdOptions: Observable<number[]>;
    shiftValues = [1, 2, 3, 4, 5, 6];
    hoursValue = [7.5, 8, 9.5, 10, 11.5, 12];
    invalidChars = ['-', '+', 'e'];
    minDate: Date = dayjs().toDate();
    maxDate: Date = null;
    viewAltShift = false;
    isRotatingShift = false;
    enteredNoteText = '';
    daysSelected: any[] = [];
    daysRequested = '';
    lengthValue = null;
    filteredFacilityIdOptions: Observable<Facility[]>;
    filteredCandidateOptions: Observable<Candidates[]>;
    minStartDate = new Date();
    reasonDialogRef: MatDialogRef<OfferReasonDialogComponent>;
    declineDialogRef: MatDialogRef<DialogSharedDeclineOfferComponent>;
    declinePanelRef: MatDialogRef<DeclinePanelComponent>;
    minEndDate: Date;
    urlParent: string;
    userId: number;
    jobProfessionName: string | null;

    contractLengthDaysInfoMessage = '';
    contractLengthDaysApprovalRequired = false;
    displayedContractLengthWeeks = 0;
    displayedContractLengthDays = 0;
    endDateTextWithoutWeeks = '';
    showRTOAlertOnStartEndDates = false;

    candidateJobProfessionValidator = (control: AbstractControl): ValidationErrors | null => {
        const professionId = control.get('professionId');
        const candidateProfessionId = control.get('candidateProfessionId');

        const result =
            professionId && candidateProfessionId && professionId.value !== candidateProfessionId.value
                ? { candidateJobProfessionDifferent: true }
                : null;
        return result;
    };

    isOfferOptimizationsEnabled = this._featureManager.isEnabledSignal(CodeBustersFeatureFlags.OfferOptimizations);

    get snapshotRtoDisplay(): string[] {
        const snapshot = this.snapshotData();
        if (!snapshot || !snapshot.candidateRequestedTimeOff?.length) {
            return [];
        }
        return this._formatRtoDates(snapshot.candidateRequestedTimeOff).split(' ');
    }

    get shiftTypeName(): string {
        if (!this.snapshotData()) {
            return '';
        }
        const shiftTypeId = this.snapshotData().jobDetails?.shiftDetails?.shiftTypeId;
        return this.lookups?.shiftTypes.find((st) => st.key === shiftTypeId)?.value ?? '';
    }

    private readonly snapshotDiffs = computed<SnapshotDiffMap>(() =>
        buildSnapshotDiffs(this.snapshotData() ?? null, this.detailsFormGroup ?? null)
    );

    private readonly diffConfig: Record<
        SnapshotDiffKey,
        { current: () => any; compare?: (c: any, s: any) => boolean }
    > = {
        contractLength: {
            current: () => normalizeNumberControl(this.detailsFormGroup?.controls?.contractLength),
            compare: (curr, snap) => Number(curr) === Number(snap)
        },
        jobId: {
            current: () => normalizeNumberControl(this.detailsFormGroup?.controls?.jobId),
            compare: (curr, snap) => Number(curr) === Number(snap)
        },
        facilityRate: { current: () => normalizeNumberControl(this.detailsFormGroup?.controls?.facilityRate) },
        ayaPaidBonus: { current: () => normalizeNumberControl(this.detailsFormGroup?.controls?.ayaPaidBonus) },
        targetMargin: { current: () => this._offerDetails?.jobTargetMargin ?? null },
        specialty: {
            current: () => {
                const id = normalizeNumberControl(this.detailsFormGroup?.controls?.expertiseId);
                return id == null ? null : (this.expertise.find((o) => o.id === id)?.name ?? null);
            }
        },
        startDate: {
            current: () => normalizeDayControl(this.detailsFormGroup?.controls?.startDate),
            compare: (curr, snap) => dayjs(curr).isSame(snap, 'day')
        },
        shiftType: { current: () => normalizeNumberControl(this.detailsFormGroup?.controls?.shiftTypeId) },
        shifts: { current: () => normalizeNumberControl(this.detailsFormGroup?.controls?.shifts) },
        shiftLength: { current: () => normalizeNumberControl(this.detailsFormGroup?.controls?.hours) },
        shiftTimeStart: { current: () => normalizeTimeControl(this.detailsFormGroup?.controls?.shiftTimeStart) },
        shiftTimeEnd: { current: () => normalizeTimeControl(this.detailsFormGroup?.controls?.shiftTimeEnd) },
        rto: {
            current: () => this.daysSelected,
            compare: (curr, snap) => areRtoListsEqual(curr, snap)
        },
        shiftsAlt: { current: () => normalizeNumberControl(this.detailsFormGroup?.controls?.shiftsAlt) },
        shiftAltLength: { current: () => normalizeNumberControl(this.detailsFormGroup?.controls?.hoursAlt) },
        shiftAltTimeStart: { current: () => normalizeTimeControl(this.detailsFormGroup?.controls?.shiftAltTimeStart) },
        shiftAltTimeEnd: { current: () => normalizeTimeControl(this.detailsFormGroup?.controls?.shiftAltTimeEnd) }
    };

    constructor(
        @Inject(WINDOW) private readonly _window: Window,
        private readonly _renderer: Renderer2,
        private readonly _formBuilder: UntypedFormBuilder,
        public dialog: MatDialog,
        public confirmationDialogService: ConfirmDialogService,
        private readonly _identityService: IdentityService,
        private readonly _offersService: SharedOffersService,
        private readonly _offersHelper: OffersHelper,
        private readonly _toasterService: ToasterService,
        private readonly _route: ActivatedRoute,
        private readonly _accountManagersService: AccountManagersService,
        private readonly _featureManager: LDFeatureManager,
        private readonly _directOfferQuoteService: DirectOfferMarginService,
        private readonly _store: Store,
        private readonly unitDescriptionSecureWindow: UnitDescriptionSecureWindowService,
        private readonly _candidateFacilityService: WfdCandidateFacilityService,
        private readonly _marginCalculatorMarginService: MarginCalculatorMarginService
    ) {}

    ngOnInit() {
        this.userId = this._identityService.current?.contactId;
        this.detailsFormGroup = this._formBuilder.group({
            id: new UntypedFormControl(''),
            contractLength: new UntypedFormControl(''),
            contractLengthDays: new UntypedFormControl(''),
            selectedCandidate: new UntypedFormControl(''),
            facilityValues: new UntypedFormControl(''),
            candidateId: new UntypedFormControl(''),
            candidateEmail: new UntypedFormControl(''),
            facilityId: new UntypedFormControl(''),
            contractTypeId: new UntypedFormControl(''),
            positionType: new UntypedFormControl(''),
            jobId: new UntypedFormControl({ value: '', disabled: true }),
            candidateProfessionId: new UntypedFormControl(''),
            professionId: new UntypedFormControl(''),
            expertiseId: new UntypedFormControl(''),
            startDate: new UntypedFormControl(''),
            endDate: new UntypedFormControl(''),
            shifts: new UntypedFormControl(''),
            shiftTypeId: new UntypedFormControl(''),
            shiftsAlt: new UntypedFormControl(''),
            hours: new UntypedFormControl(''),
            hoursAlt: new UntypedFormControl(''),
            shiftTimeOfDayId: new UntypedFormControl(''),
            shiftAltTimeOfDayId: new UntypedFormControl(''),
            shiftTimeStart: new UntypedFormControl(''),
            shiftTimeEnd: new UntypedFormControl(''),
            shiftAltTimeStart: new UntypedFormControl(''),
            shiftAltTimeEnd: new UntypedFormControl(''),
            shiftM: new UntypedFormControl(''),
            shiftH: new UntypedFormControl(''),
            shiftToM: new UntypedFormControl(''),
            shiftToH: new UntypedFormControl(''),
            shiftAltM: new UntypedFormControl(''),
            shiftAltH: new UntypedFormControl(''),
            shiftAltToM: new UntypedFormControl(''),
            shiftAltToH: new UntypedFormControl(''),
            shiftDetails: new UntypedFormControl(''),
            hasRequestedTimeOff: new UntypedFormControl(''),
            requestedTimeOff: new UntypedFormControl(''),
            requestedTimeOffIsApproved: new UntypedFormControl(''),
            vendorContactId: new UntypedFormControl(''),
            vendorRate: new UntypedFormControl(''),
            facilityRate: new UntypedFormControl(''),
            otherDetails: new UntypedFormControl(''),
            status: new UntypedFormControl({ value: '', disabled: true }),
            isContractRequestStatus: new UntypedFormControl({ value: '', disabled: true }),
            specialRequestNote: new UntypedFormControl(''),
            offerType: new UntypedFormControl(''),
            isRemoteBooking: new UntypedFormControl(''),
            rtoApprovalStatus: new UntypedFormControl(''),
            ayaPaidBonus: new UntypedFormControl(''),
            pipelineRequestInstructions: new UntypedFormControl('')
        });

        const shiftTypeIdCtrl = this.detailsFormGroup.controls.shiftTypeId;
        const shiftsCtrl = this.detailsFormGroup.controls.shifts;
        const hoursCtrl = this.detailsFormGroup.controls.hours;
        const shiftTimeOfDayIdCtrl = this.detailsFormGroup.controls.shiftTimeOfDayId;

        shiftTypeIdCtrl.setValidators(Validators.required);
        shiftsCtrl.setValidators(Validators.required);
        hoursCtrl.setValidators(Validators.required);
        shiftTimeOfDayIdCtrl.setValidators(Validators.required);

        shiftTypeIdCtrl.updateValueAndValidity();
        shiftsCtrl.updateValueAndValidity();
        hoursCtrl.updateValueAndValidity();
        shiftTimeOfDayIdCtrl.updateValueAndValidity();

        if (this.detailsFormGroup) {
            this.detailsFormGroup
                .get('shiftTypeId')
                .valueChanges.pipe(untilDestroyed(this))
                .subscribe((shift) => {
                    this.validateShiftFields(shift);
                });
        }

        this.filteredCandidateOptions = this.detailsFormGroup.get('selectedCandidate').valueChanges.pipe(
            startWith(''),
            map((val) => {
                if (val) {
                    return this.filterCandidates(val);
                } else {
                    return this.candidates.slice();
                }
            })
        );

        this.filteredFacilityIdOptions = this.detailsFormGroup.get('facilityValues').valueChanges.pipe(
            startWith(''),
            map((val) => {
                if (val) {
                    return this.filterFacilities(val);
                } else {
                    if (this.facilityLookups) {
                        this.facilityLookups.professions = [];
                        this.facilityLookups.jobs = [];
                        this.facilityLookups.accountManager = '';
                    }
                    return this.facilities.slice();
                }
            })
        );
        this.showDaysRequested();
        this._route.parent.url.subscribe((url) => (this.urlParent = url[0].path));

        this.detailsFormGroup.addValidators(this.candidateJobProfessionValidator);

        this._store.dispatch(getSubmittalStatuses());

        this._featureManager
            .isEnabled(FeatureFlag.ConnectVMSRecruiterSpecificOfferNotes)
            .pipe(untilDestroyed(this))
            .subscribe((isEnabled) => {
                this.isRecruiterSpecificNoteFlagEnabled = isEnabled;
            });

        this.offerRescindedSubStauses$ = this._store.select(offerRescindedSubStatuses);

        if (this.isOfferOptimizationsEnabled()) {
            const pipelineRequestInstructionsCtrl = this.detailsFormGroup.controls.pipelineRequestInstructions;
            pipelineRequestInstructionsCtrl.setValidators([Validators.maxLength(500)]);
            pipelineRequestInstructionsCtrl.updateValueAndValidity();
        }

        const otherDetailsCtrl = this.detailsFormGroup.controls.otherDetails;
        otherDetailsCtrl.setValidators([Validators.maxLength(1500)]);
        otherDetailsCtrl.updateValueAndValidity();
    }

    isDifferent(key: SnapshotDiffKey): boolean {
        const entry = this.diffConfig[key];
        if (!entry) {
            return false;
        }
        return isOfferSnapshotDifferent(this.snapshotDiffs(), key, entry.current(), entry.compare);
    }

    get validateCandidateProfessionJobs(): boolean {
        if (this.editMode) {
            const jobProfessionIdControl = this.detailsFormGroup.controls.professionId;
            const candidateProfessionIdControl = this.detailsFormGroup.controls.candidateProfessionId;

            return (
                jobProfessionIdControl &&
                candidateProfessionIdControl &&
                jobProfessionIdControl.value !== candidateProfessionIdControl.value
            );
        } else {
            return false;
        }
    }

    getCandidateOfferChangeData() {
        const changeData: CandidateOfferChangeData[] = [];
        for (const keyLabel of SnapshotDiffKeyLabel) {
            const key = keyLabel.key as SnapshotDiffKey;
            if (this.isDifferent(key)) {
                const changeItem: CandidateOfferChangeData = {
                    property: keyLabel.label,
                    valueFrom: this.formatValueBasedOnKey(key, this.snapshotDiffs()[key]),
                    valueTo: this.formatValueBasedOnKey(key, this.diffConfig[key].current())
                };
                changeData.push(changeItem);
            }
        }
        return changeData;
    }

    formatValueBasedOnKey(key: SnapshotDiffKey, value: any): string {
        if (value == null) {
            return '';
        }

        switch (key) {
            case 'facilityRate':
            case 'ayaPaidBonus':
                return `$${value}`;
            case 'shiftLength':
                return `${value} Hrs`;
            case 'contractLength':
                return `${value} Weeks`;
            case 'targetMargin':
                return `${value}%`;
            case 'startDate':
                return dayjs(value).format(this.DATE_FORMAT_STRING);
            case 'rto':
                return this._formatRtoDates(value);
            default:
                return `${value}`;
        }
    }

    private validateShiftFields(shift: any) {
        const shiftsAltCtrl = this.detailsFormGroup.controls.shiftsAlt;
        const hoursAltCtrl = this.detailsFormGroup.controls.hoursAlt;
        const shiftAltTimeOfDayIdCtrl = this.detailsFormGroup.controls.shiftAltTimeOfDayId;

        this.isRotatingShift = shift === 3;
        if (this.isRotatingShift) {
            // when flag is on, clear alt shifts, hours values for rotating shift
            shiftsAltCtrl.patchValue('');
            hoursAltCtrl.patchValue('');
        }

        this.viewAltShift = shift === 2 ? true : false;

        shiftsAltCtrl.clearValidators();
        hoursAltCtrl.clearValidators();
        shiftAltTimeOfDayIdCtrl.clearValidators();

        if (this.isRotatingShift) {
            shiftAltTimeOfDayIdCtrl.setValidators(Validators.required);
        } else if (this.viewAltShift) {
            shiftsAltCtrl.setValidators(Validators.required);
            hoursAltCtrl.setValidators(Validators.required);
            shiftAltTimeOfDayIdCtrl.setValidators(Validators.required);
        }

        shiftsAltCtrl.updateValueAndValidity();
        hoursAltCtrl.updateValueAndValidity();
        shiftAltTimeOfDayIdCtrl.updateValueAndValidity();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.isOfferOptimizationsEnabled() && changes?.offerDetails) {
            this.initialOfferValues = null;
            this.setOfferValuesOnLoad();
        }

        this.isReturnerOnChange(changes);
        this.getCandidatePriorityOnChange(changes);

        if (changes.startDate?.currentValue) {
            this.detailsFormGroup.get('startDate').patchValue(changes.startDate.currentValue);
        }
        if (changes.endDate?.currentValue) {
            if (this.detailsFormGroup.get('startDate').value) {
                this.detailsFormGroup.get('endDate').patchValue(changes.endDate.currentValue);
                this.validateRTODatesforEditOrViewOffer();
            }
        }
        if (changes.vendorOptions?.currentValue && this.declineDialogRef) {
            this.declineDialogRef.componentInstance.data.vendorOptions = changes.vendorOptions.currentValue;
            this.declineDialogRef.componentInstance.isAgencyLoading = false;
        }
        if (changes.facilityOptions?.currentValue && this.declineDialogRef) {
            this.declineDialogRef.componentInstance.data.facilityOptions = changes.facilityOptions.currentValue;
            this.declineDialogRef.componentInstance.isFacilityLoading = false;
        }
        if (changes.facilityOptions?.currentValue && this.declinePanelRef) {
            this.declinePanelRef.componentInstance.data.facilityOptions = changes.facilityOptions.currentValue;
            this.declinePanelRef.componentInstance.isFacilityLoading = false;
        }
        if (changes.vendorOptions?.currentValue && this.declinePanelRef) {
            this.declinePanelRef.componentInstance.data.vendorOptions = changes.vendorOptions.currentValue;
            this.declinePanelRef.componentInstance.isAgencyLoading = false;
        }
    }

    hasError = (controlName: string, errorName: string) => {
        return this.detailsFormGroup.controls[controlName].hasError(errorName);
    };

    setAgencyOfferFieldsAndValidations(): void {
        this.detailsFormGroup.get('selectedCandidate').setValidators([Validators.required]);
        this.detailsFormGroup.get('facilityValues').setValidators([Validators.required]);
        this.detailsFormGroup.get('vendorContactId').setValidators([Validators.required]);
    }

    setDirectOfferFieldsAndValidattions(): void {
        if (this.detailsFormGroup) {
            this.detailsFormGroup.get('startDate').setValidators([Validators.required]);
            this.detailsFormGroup.get('endDate').setValidators([Validators.required]);
        }
    }

    setContractDateFieldsValidations(): void {
        if (this.detailsFormGroup) {
            this.detailsFormGroup.get('startDate').setValidators([Validators.required]);
            this.detailsFormGroup.get('endDate').setValidators([Validators.required]);
            this.detailsFormGroup.get('contractLength').setValidators([Validators.required]);
            this.detailsFormGroup.get('contractLengthDays').setValidators([Validators.required]);
        }
    }

    filterCandidates(val: any) {
        return val?.id
            ? this.candidates.filter((candidates) => candidates.id === val.id)
            : this.candidates.filter((candidates) =>
                  candidates.name ? candidates.name.toLowerCase().startsWith(val.toLowerCase()) : ''
              );
    }

    filterFacilities(val: any) {
        return val?.id
            ? this.facilities.filter((facility) => facility.id === val.id)
            : this.facilities.filter((facility) =>
                  facility.name ? facility.name.toLowerCase().startsWith(val.toLowerCase()) : ''
              );
    }

    filterJobIds(val: any) {
        const candidateProfessionId = this.detailsFormGroup.get('candidateProfessionId').value;
        return this._offersService.filterJobIds(val, this.facilityLookups, candidateProfessionId);
    }

    displayJobIds(id: any) {
        if (id) {
            return id;
        }
        return null;
    }

    focusOutContract() {
        const val = this.detailsFormGroup.get('contractLength').value;
        const start = this.detailsFormGroup.get('startDate').value;
        if (Number.isInteger(val)) {
            if (start) {
                this.detailsFormGroup.get('endDate').patchValue('');
            }
            this.calculateContract();
        }
    }

    goBack() {
        this.goBackClicked.emit();
    }

    enableEditMode() {
        this.enableEdit.emit();
        this.rateEnabled = false;
    }

    enableViewMode() {
        this.enableView.emit();
        this.rateEnabled = false;

        if (this.isOfferOptimizationsEnabled()) {
            this.clearEditModeValues();
        }
    }

    patchEditValues() {
        const timeStart = dayjs().toDate();
        const timeEnd = dayjs().toDate();
        const timeAltStart = dayjs().toDate();
        const timeAltEnd = dayjs().toDate();

        timeStart.setHours(this._offerDetails.shiftH);
        timeStart.setMinutes(this._offerDetails.shiftM);

        timeEnd.setHours(this._offerDetails.shiftToH);
        timeEnd.setMinutes(this._offerDetails.shiftToM);

        timeAltStart.setHours(this._offerDetails.shiftAltH);
        timeAltStart.setMinutes(this._offerDetails.shiftAltM);

        timeAltEnd.setHours(this._offerDetails.shiftAltToH);
        timeAltEnd.setMinutes(this._offerDetails.shiftAltToM);

        this.detailsFormGroup.patchValue({
            ...this._offerDetails,
            shiftTimeStart: timeStart,
            shiftTimeEnd: timeEnd,
            shiftAltTimeStart: timeAltStart,
            shiftAltTimeEnd: timeAltEnd,
            contractLength: this.displayedContractLengthWeeks,
            contractLengthDays: this.displayedContractLengthDays
        });

        this.daysSelected = this._offerDetails.requestedTimeOff;
        this.showDaysRequested();
        this.calculateContract(null, true);

        this.detailsFormGroup.patchValue({
            professionId: this._offerDetails.professionId
        });
        this.jobProfessionName = this._offerDetails.professionName;
    }

    jobIdSelection(event): void {
        const val = event.option?.value || event.target?.value;
        if (val) {
            this.jobIdSelectionChange.emit(val);
        }
    }

    startDateSelected(event): void {
        const isBefore = dayjs(this.detailsFormGroup.get('endDate').value).isBefore(event.value);
        if (isBefore) {
            this.detailsFormGroup.patchValue({ endDate: null });
        }
        this.minEndDate = new Date(event.value);
        this.calculateContract();
    }

    facilitySelection(event) {
        this.detailsFormGroup.patchValue({ jobId: '' });
        this.detailsFormGroup.patchValue({ facilityId: event.option.value.id });
        this.facilitySelectionChange.emit(event.option.value.id);
    }

    professionSelection(professionId: number, checkRateUpdateFields = true) {
        this.professionSelectionChange.emit(professionId);
        this.detailsFormGroup.patchValue({ expertiseId: null });
        if (checkRateUpdateFields) {
            this.checkRateUpdateFields('Profession');
        }
    }

    candidateSelection(event) {
        const candidate = event.option.value as Candidates;
        if (!candidate) {
            return;
        }

        const candidateProfessionId = this.detailsFormGroup.get('candidateProfessionId').value;
        if (this._offerDetails && candidateProfessionId !== candidate.professionId) {
            this._offerDetails.professionId = candidate.professionId;
            this.detailsFormGroup.patchValue({
                candidateProfessionId: candidate.professionId,
                jobId: ''
            });
            this.professionSelection(candidate.professionId);
        }

        this.detailsFormGroup.patchValue({
            candidateId: candidate.userId,
            candidateEmail: candidate.email,
            vendorContactId: candidate.vendorContactId
        });
    }

    calculateContract(endChanged = null, firstLoad = false) {
        const obj = {
            endChanged: endChanged,
            contractLength: this.detailsFormGroup.get('contractLength').value,
            jobId: this.detailsFormGroup.get('jobId').value,
            startDate: this.detailsFormGroup.get('startDate').value,
            endDate: this.detailsFormGroup.get('endDate').value,
            firstLoad: firstLoad
        };
        this.calculateContractDetails.emit(obj);
    }

    calculateContractLength(): number {
        if (this.detailsFormGroup) {
            return Math.round(
                (new Date(this.detailsFormGroup.get('endDate').value).getTime() -
                    new Date(this.detailsFormGroup.get('startDate').value).getTime()) /
                    (7 * 24 * 60 * 60 * 1000)
            );
        } else {
            return 0;
        }
    }

    getDuration(start: Date, end: Date) {
        const startInput = dayjs(start);
        const endInput = dayjs(end);
        const duration = dayjs(endInput).diff(startInput, 'days');
        return `${duration} days`;
    }

    get offerStartDate(): Date | null {
        return this.offerData?.canEdit === false
            ? dayjs(this.offerData?.startDate).toDate()
            : (this.detailsFormGroup.get('startDate')?.value as Date | null);
    }

    get offerEndDate(): Date | null {
        return this.offerData?.canEdit === false
            ? dayjs(this.endDateTextWithoutWeeks).toDate()
            : (this.detailsFormGroup.get('endDate')?.value as Date | null);
    }

    get offerTimeOffDates(): Date[] {
        return this.offerData?.offerTimeOffDates?.map((date) => dayjs(date).toDate()) || [];
    }

    openRescindReasonDialog() {
        this.isOperationProcessing = true;
        this.offerRescindedSubStauses$.pipe(untilDestroyed(this)).subscribe((result: SubmittalSubStatus[]) => {
            const list = result.map((val) => {
                return { id: val.id, name: val.name } as unknown as ListItem;
            });

            this.loadRescindDialog(list);
        });
    }

    loadRescindDialog(reasons: ListItem[]) {
        this.dialog
            .open(OfferReasonDialogComponent, {
                data: { id: this.offerData.id, reasons }
            })
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    const offerReasonResult: OfferReasonResult = result;
                    offerReasonResult.submittalSubStatus = result.reasonId;
                    offerReasonResult.reasonId = null;
                    this.rescind.emit(offerReasonResult);
                }
                this.isOperationProcessing = false;
            });
    }

    refreshShiftEndTime() {
        const hours = this.detailsFormGroup.get('hours').value;
        const shiftTimeStart = this.detailsFormGroup.get('shiftTimeStart').value;
        if (hours && shiftTimeStart) {
            const endTime = this._offersHelper.getShiftEndTime(hours, shiftTimeStart);
            this.detailsFormGroup.patchValue({ shiftTimeEnd: endTime });
            this.onTimeEndChanged(endTime);
        }
    }

    refreshShiftEndTimeAlt() {
        const hoursAlt = this.detailsFormGroup.get('hoursAlt').value;
        const shiftAltTimeStart = this.detailsFormGroup.get('shiftAltTimeStart').value;
        if (hoursAlt && shiftAltTimeStart) {
            const endTimeAlt = this._offersHelper.getShiftEndTime(hoursAlt, shiftAltTimeStart);
            this.detailsFormGroup.patchValue({ shiftAltTimeEnd: endTimeAlt });
            this.onAltTimeEndChanged(endTimeAlt);
        }
    }

    onShiftLengthAltChanged() {
        this.refreshShiftEndTimeAlt();
    }

    onTimeStartChanged(value: Date) {
        const hour = dayjs(value).format('HH');
        const min = dayjs(value).format('mm');
        this.detailsFormGroup.patchValue({
            shiftH: Number(hour),
            shiftM: Number(min)
        });

        this.refreshShiftEndTime();
    }

    onAltTimeStartChanged(value: Date) {
        const hour = dayjs(value).format('HH');
        const min = dayjs(value).format('mm');
        this.detailsFormGroup.patchValue({
            shiftAltH: Number(hour),
            shiftAltM: Number(min)
        });

        this.refreshShiftEndTimeAlt();
    }

    onTimeEndChanged(value: Date) {
        this.detailsFormGroup.patchValue({
            shiftToH: value.getHours(),
            shiftToM: value.getMinutes()
        });
    }

    onAltTimeEndChanged(value: Date) {
        this.detailsFormGroup.patchValue({
            shiftAltToH: value.getHours(),
            shiftAltToM: value.getMinutes()
        });
    }

    dateClass(): (date: Date) => MatCalendarCellCssClasses {
        return (date: Date): MatCalendarCellCssClasses => {
            return RtoDatesHelpers.getCssClassForDate(date, this.offerStartDate, this.offerEndDate, this.daysSelected);
        };
    }

    dateChanged(event: string): void {
        const foundDateIndex = this.daysSelected.findIndex((date) => dayjs(date).isSame(dayjs(event)));
        const dateNode = this._getDateNode(event);
        let formattedDate = [];

        if (foundDateIndex < 0) {
            this.daysSelected.push(event);
            this.daysSelected = this._sortDates(this.daysSelected);

            this.daysSelected.forEach((d) => {
                const day = dayjs(d).format();
                return formattedDate.push(day);
            });
            this.detailsFormGroup.get('requestedTimeOff').patchValue(formattedDate);
            const isStartOrEndDate = RtoDatesHelpers.addCssClassForOfferStartEndDates(
                new Date(event),
                this.offerStartDate,
                this.offerEndDate
            );
            this._renderer.addClass(
                dateNode,
                isStartOrEndDate ? this.constDateContractStartEndClass : this.constDateSelected
            );
        } else {
            this.daysSelected.splice(foundDateIndex, 1);
            this.daysSelected = this._sortDates(this.daysSelected);
            formattedDate = [];
            this.daysSelected.forEach((d) => {
                const day = dayjs(d).format();
                return formattedDate.push(day);
            });
            this.detailsFormGroup.get('requestedTimeOff').patchValue(formattedDate);

            this._renderer.removeClass(dateNode, this.constDateSelected);
            this._renderer.removeClass(dateNode, this.constDateContractStartEndClass);
        }

        this.showDaysRequested();
    }

    clearDates() {
        this.daysSelected = [];
        this.calendar.updateTodaysDate();
        this.clearCalendarTrigger.closeMenu();
        this.detailsFormGroup.get('requestedTimeOff').patchValue([]);
        this.showDaysRequested();
        this.validateRTODatesforEditOrViewOffer();
    }

    showDaysRequested(): void {
        if (this.daysSelected.length === 0) {
            this.daysRequested = '';
            this.validateRTODatesforEditOrViewOffer();
            return;
        }

        const vacationDates = this.daysSelected.map((date) => dayjs(date).toDate());
        this.daysRequested = MarginCalculatorStateHelpers.combinedDateRangeDisplay(vacationDates);

        this.validateRTODatesforEditOrViewOffer();
    }

    validateRTODatesforEditOrViewOffer() {
        const vacationList = this.offerData?.canEdit === false ? this.offerTimeOffDates : this.daysSelected;
        this.showRTOAlertOnStartEndDates = RtoDatesHelpers.shouldDisplayRTOAlertOnStartOrEndDate(
            this.offerStartDate,
            this.offerEndDate,
            vacationList
        );
    }

    addNote() {
        if (this.userType === OffersUserType.Recruiter) {
            this.addNoteEmit.emit({ note: this.enteredNoteText, title: 'Note' });
        } else {
            this.addNoteEmit.emit(this.enteredNoteText);
        }

        this.enteredNoteText = '';
    }

    addNoteText(noteText: string) {
        if (this.userType === OffersUserType.Recruiter) {
            this.addNoteEmit.emit({ note: noteText, title: 'Note' });
        } else {
            this.addNoteEmit.emit(noteText);
        }
    }

    addNoteWithNoteTextAndCategory(note: OfferNoteAndCategory) {
        this.addNoteEmit.emit({
            note: note.note,
            offerNoteCategoryId: note.category as CandidateOfferNoteCategory
        });
    }

    addNoteWithCategory(offerNoteCategoryId: CandidateOfferNoteCategory) {
        this.addNoteEmit.emit({
            note: this.enteredNoteText,
            offerNoteCategoryId
        });

        this.enteredNoteText = '';
    }

    updateStatusShowAPN() {
        this.updateShowAPN.emit();
    }

    updateStatusAPNReview() {
        this.updateAPNReview.emit();
    }

    updateStatusShowVendor() {
        if (this.detailsFormGroup.value.facilityRate > 0) {
            this.updateShowVendor.emit();
        } else {
            this.checkFacilityRate('Vendor Review');
        }
    }

    updateStatusShowRecruiter() {
        this.showToRecruiter.emit();
    }

    openContractRequestDialog(updateStatusOnly: boolean) {
        const expertiseId = this.detailsFormGroup.value.expertiseId;
        const candidateProfessionId = this.detailsFormGroup.value.candidateProfessionId;
        if (candidateProfessionId && !expertiseId) {
            this._toasterService.toast('Cannot update the offer. Expertise is empty.', 20000);
            return;
        }

        if (this.detailsFormGroup.value.facilityRate > 0) {
            this.confirmationDialogService
                .showDialog({
                    title: 'Contract Request',
                    message:
                        'You are submitting a contract request without showing to vendor. Are you sure you would like to proceed?',
                    action: 'Confirm',
                    cancelText: 'Cancel'
                })
                .subscribe((isContractRequestConfirmed) => {
                    if (isContractRequestConfirmed) {
                        const dialogConfig = {
                            maxWidth: '600px',
                            disableClose: true,
                            panelClass: ['nova__dialog', 'complete-offer-dialog'],
                            data: {
                                shiftDetails: this.detailsFormGroup.value.shiftDetails,
                                otherDetails: this.detailsFormGroup.value.otherDetails
                            }
                        };
                        this.dialog
                            .open(DialogContractRequestedOfferComponent, dialogConfig)
                            .afterClosed()
                            .subscribe((result) => {
                                if (result?.send) {
                                    const specialRequestNote = result.requestStr;
                                    if (updateStatusOnly) {
                                        this.updateContractRequestedOffer.emit(specialRequestNote);
                                    } else {
                                        this.updateOfferAndRequestContract(specialRequestNote);
                                    }
                                }
                            });
                    }
                });
        } else {
            this.checkFacilityRate('Contract Request');
        }
    }

    openMargin(marginId: number) {
        if (marginId) {
            window.open(
                `${window.location.origin}/#/margin-calculator/${this._identityService.current.contactId}/margin/${marginId}`,
                '_blank',
                'width=1366,height=768,resizable=1,scrollbars=1,titlebar=0,location=0,status=0'
            );
        }
    }

    openMarginCalculator(marginId: number) {
        if (this.offerData?.isSubcontractor) {
            this._window.open(
                `${this._window.location.origin}/#/subcontractor-margin-calculator/${this._identityService.current.contactId}/margin/${marginId}`,
                '_blank',
                'width=1366,height=970,resizable=1,scrollbars=1,titlebar=0,location=0,status=0'
            );
        } else {
            this._window.open(
                `${this._window.location.origin}/#/margin-calculator/${this._identityService.current.contactId}/margin/${marginId}`,
                '_blank',
                'width=1366,height=768,resizable=1,scrollbars=1,titlebar=0,location=0,status=0'
            );
        }
    }

    updateOfferAndRequestContract(specialRequest: string) {
        const data = this.detailsFormGroup.value;
        data.updatedStatusId = OfferStatus.ContractRequested;
        data.specialRequestNote = specialRequest;
        this.updateOffer();
    }

    updateOffer(vendor = false) {
        const data = this.detailsFormGroup.value;
        if (vendor) {
            data.updatedStatusId = OfferStatus.VendorReview;
        } else if (this.offerData.canReactivateOffer) {
            data.updatedStatusId = OfferStatus.Pending;
        } else {
            if (this.detailsFormGroup.get('isContractRequestStatus').value && isEmpty(data.updatedStatusId)) {
                data.updatedStatusId = OfferStatus.ContractChange;
            }
        }

        if (data.shiftTypeId) {
            data.hasShifts = true;
        }

        if (!data.hasRequestedTimeOff) {
            data.requestedTimeOff = null;
        }

        if (this.isOfferOptimizationsEnabled()) {
            const changeData = this.getCandidateOfferChangeData();
            if (changeData.length > 0) {
                data.changeDataNote = {
                    isFromSnapshot: false,
                    changeData: changeData
                };
            }
        }
        this.updateOfferEmit.emit(data);
        this.addUpdatedFieldsNotes();
    }

    addUpdatedFieldsNotes() {
        let notes = '';
        const dateFormat = this.DATE_FORMAT_STRING;
        const locale = 'en';
        let contractLength = false;

        const data = this.detailsFormGroup.value;

        if (data.contractTypeId !== this.offerDetails?.contractTypeId) {
            notes = [notes, 'Contract Type'].filter(Boolean).join(', ');
        }
        if (data.jobId !== this.offerDetails?.jobId) {
            notes = [notes, 'Job Id'].filter(Boolean).join(', ');
        }
        if (data.expertiseId !== this.offerDetails?.expertiseId) {
            notes = [notes, 'Specialty'].filter(Boolean).join(', ');
        }
        if (
            formatDate(data.startDate ? data.startDate : null, dateFormat, locale) !==
            formatDate(this.offerDetails?.startDate ? this.offerDetails?.startDate : null, dateFormat, locale)
        ) {
            notes = [notes, 'Start Date'].filter(Boolean).join(', ');
            contractLength = true;
        }
        if (
            formatDate(data.endDate ? data.endDate : null, dateFormat, locale) !==
            formatDate(this.offerDetails?.endDate ? this.offerDetails?.endDate : null, dateFormat, locale)
        ) {
            notes = [notes, 'End Date'].filter(Boolean).join(', ');
            contractLength = true;
        }
        if (contractLength === true) {
            notes = [notes, 'Contract Length'].filter(Boolean).join(', ');
        }

        if (data.shiftTypeId !== this.offerDetails?.shiftTypeId) {
            notes = [notes, 'Shift Type'].filter(Boolean).join(', ');
        }
        if (data.shifts !== this.offerDetails?.shifts || data.shiftsAlt !== this.offerDetails?.shiftsAlt) {
            notes = [notes, '# of Shifts'].filter(Boolean).join(', ');
        }
        if (data.hours !== this.offerDetails?.hours || data.hoursAlt !== this.offerDetails?.hoursAlt) {
            notes = [notes, 'Shift Length'].filter(Boolean).join(', ');
        }
        if (
            data.shiftTimeOfDayId !== this.offerDetails?.shiftTimeOfDayId ||
            data.shiftAltTimeOfDayId !== this.offerDetails?.shiftAltTimeOfDayId
        ) {
            notes = [notes, 'Shift Time of Day'].filter(Boolean).join(', ');
        }
        if (
            data.shiftH !== this.offerDetails?.shiftH ||
            data.shiftM !== this.offerDetails?.shiftM ||
            data.shiftAltH !== this.offerDetails?.shiftAltH ||
            data.shiftAltM !== this.offerDetails?.shiftAltM
        ) {
            notes = [notes, 'Shift Time'].filter(Boolean).join(', ');
        }
        if (data.shiftDetails !== this.offerDetails?.shiftDetails) {
            notes = [notes, 'Shift Details'].filter(Boolean).join(', ');
        }
        if (data.hasRequestedTimeOff !== this.offerDetails?.hasRequestedTimeOff) {
            notes = [notes, 'RTO'].filter(Boolean).join(', ');
        }
        if (
            data.requestedTimeOff?.length !== this.offerDetails?.requestedTimeOff?.length ||
            data.requestedTimeOff?.filter((x) => !this.offerDetails?.requestedTimeOff?.includes(x)).length > 0
        ) {
            notes = [notes, 'RTO Dates'].filter(Boolean).join(', ');
        }
        if (data.requestedTimeOffIsApproved !== this.offerDetails?.requestedTimeOffIsApproved) {
            notes = [notes, 'RTO Approval'].filter(Boolean).join(', ');
        }
        if (data.rtoApprovalStatus !== this.offerDetails?.rtoApprovalStatus) {
            notes = [notes, 'RTO Approval'].filter(Boolean).join(', ');
        }
        if (data.facilityRate !== this.offerDetails?.facilityRate) {
            notes = [notes, 'Facility Rate'].filter(Boolean).join(', ');
        }
        if (data.otherDetails !== this.offerDetails?.otherDetails) {
            notes = [notes, 'Other Details'].filter(Boolean).join(', ');
        }

        if (notes.length > 0) {
            this.addNoteEmit.emit(notes);
        }
    }

    attachmentToUpload(file) {
        this.attachmentUpload.emit(file);
    }

    attachmentToUpdate(file) {
        this.attachmentUpdate.emit(file);
    }

    attachmentToDelete(file) {
        this.attachmentDelete.emit(file);
    }

    attachmentToGet(file) {
        this.attachmentGetFile.emit(file);
    }

    checkRateUpdateFields(changeField: string): void {
        if (
            !!this.detailsFormGroup.get('facilityId').value &&
            !!this.detailsFormGroup.get('candidateProfessionId').value &&
            !!this.detailsFormGroup.get('expertiseId').value &&
            !!this.detailsFormGroup.get('hours').value
        ) {
            const ratesRequest = {
                facilityId: this.detailsFormGroup.get('facilityId').value,
                professionId: this.detailsFormGroup.get('candidateProfessionId').value,
                expertiseId: this.detailsFormGroup.get('expertiseId').value,
                hours: this.detailsFormGroup.get('hours').value,
                shiftTimeOfDayId: this.detailsFormGroup.get('shiftTimeOfDayId').value,
                changeField: changeField
            };
            this.updateRatesEmit.emit(ratesRequest);
        }
        if (changeField === 'Shift Length') {
            this.refreshShiftEndTime();
        }
    }

    displayFieldName(option: any) {
        if (option?.name) {
            return option.name;
        }
        return '';
    }

    updateVendorRate(): void {
        const vendorRateRequest = {
            facilityId: this.detailsFormGroup.get('facilityId').value,
            facilityRate: this.detailsFormGroup.get('facilityRate').value
        };
        this.updateVendorRateEmit.emit(vendorRateRequest);
    }

    openFacility(facilityId: number) {
        const facilityIdVal = facilityId ? facilityId : this.detailsFormGroup.get('facilityId').value;
        this.openFacilityLink.emit(facilityIdVal);
    }

    rateEdit() {
        this.rateEnabled = !this.rateEnabled;
    }

    validateInput(event) {
        if (this.invalidChars.includes(event.key)) {
            event.preventDefault();
        }
    }

    checkFacilityRate(text: string) {
        this.facilityRateCheck.emit(text);
    }

    togglePanel() {
        this.isPanelOpen = !this.isPanelOpen;
    }

    declineOffer() {
        this.declinePanelRef = this.dialog.open(DeclinePanelComponent, {
            position: { right: '0px', top: '0px' },
            disableClose: true,
            data: {
                title: 'Decline Offer',
                declineReasons: this.declineReasons,
                vendorOptions: this.vendorOptions,
                facilityOptions: this.facilityOptions
            }
        });

        this.declinePanelRef.componentInstance.doVendorSearch.pipe(untilDestroyed(this)).subscribe((vendorInput) => {
            this.doVendorSearch.emit(vendorInput);
        });
        this.declinePanelRef.componentInstance.doFacilitySearch
            .pipe(untilDestroyed(this))
            .subscribe((facilityInput) => {
                this.doFacilitySearch.emit(facilityInput);
            });

        this.declinePanelRef.afterClosed().subscribe((result) => {
            if (result) {
                this.declineOfferEmit.emit(result);
            }
        });
    }

    requestChanges() {
        const dialogConfig = {
            disableClose: true,
            panelClass: ['nova__dialog', 'request-changes-dialog'],
            data: {}
        };
        this.dialog
            .open(DialogRequestedChangesComponent, dialogConfig)
            .afterClosed()
            .subscribe((result) => {
                if (result) {
                    // TBD emit changes
                }
            });
    }

    isWFDEmploymentType(): boolean {
        return this.offerData?.jobEmploymentTypeCode === EmploymentTypeCode.WorkforceDisruption;
    }

    isReturnerOnChange(changes: SimpleChanges) {
        if (
            changes.offer?.currentValue.candidateId &&
            (changes.offer?.currentValue.hospitalTypeId === HospTypeEnum.WorkforceDisruptionClinical ||
                changes.offer?.currentValue.hospitalTypeId === HospTypeEnum.WorkforceDisruptionNonClinical)
        ) {
            this.isWfdReturner$ = this._candidateFacilityService.isWfdReturner(changes.offer?.currentValue.candidateId);
        }
    }

    getCandidatePriorityOnChange(changes: SimpleChanges) {
        if (
            changes.offer?.currentValue.candidateId &&
            changes.offer?.currentValue.facilityId &&
            (changes.offer?.currentValue.hospitalTypeId === HospTypeEnum.WorkforceDisruptionClinical ||
                changes.offer?.currentValue.hospitalTypeId === HospTypeEnum.WorkforceDisruptionNonClinical)
        ) {
            this.wfdPriority$ = this._candidateFacilityService
                .getCandidateWfdPriority(
                    changes.offer?.currentValue.candidateId,
                    changes.offer?.currentValue.facilityId
                )
                .pipe(map((response: any) => response?.wfdPriorityLevelId));
        }
    }

    showSampleMarginButton(): boolean {
        return this.isWFDEmploymentType() && this._showMarginButtons() && isEmpty(this.marginId);
    }

    showCreateAndImportMarginButtons(): boolean {
        return this._showMarginButtons() && !this.marginId && !this.isWFDEmploymentType();
    }

    showImportMarginButton(): boolean {
        return this._showMarginButtons() && !isEmpty(this.marginId) && !this.isWFDEmploymentType();
    }

    private _showMarginButtons(): boolean {
        return (
            this.userType === OffersUserType.Recruiter &&
            this._identityService.hasPermission('Recruiting', 'Deals') &&
            this.offerData?.jobId > 0 &&
            this.offerData?.facilityId > 0 &&
            this.offerData?.candidateId > 0 &&
            this.offerData?.offerType === OfferType.Direct &&
            (this.offerData?.statusId === OfferStatus.RecruiterReview ||
                this.offerData?.statusId === OfferStatus.ContractRequested)
        );
    }

    showCreateMarginDialog(): void {
        this.dialog
            .open(DirectOfferCreateMarginDialogComponent, {
                panelClass: 'direct-offer-margin-dialog',
                data: {
                    selectedJob: {
                        id: this.offerData?.jobId,
                        facilityId: this.offerData?.facilityId,
                        facilityName: this.offerData?.facilityName,
                        shiftsText: this.offerData?.shiftsText,
                        jobTitle: this.offerData?.jobTitle
                    },
                    recruiterId: this._identityService.current.contactId,
                    candidateId: this.offerData?.candidateId,
                    candidateName: this.offerData?.candidateName,
                    offerId: this.offerId
                }
            })
            .afterClosed()
            .subscribe((marginId) => {
                if (marginId) {
                    this._marginCreated(marginId);
                }
            });
    }

    showImportMarginDialog(): void {
        this.dialog
            .open(DirectOfferImportMarginDialogComponent, {
                panelClass: 'direct-offer-import-dialog',
                data: {
                    facilityId: this.offerData?.facilityId,
                    facilityName: this.offerData?.facilityName,
                    offerId: this.offerId
                }
            })
            .afterClosed()
            .subscribe((marginId) => {
                if (marginId) {
                    this._marginCreated(marginId);
                }
            });
    }

    openMarginDifferences() {
        this.dialog.open(OfferMarginDifferencesDialogComponent, {
            panelClass: 'nova__dialog',
            data: {
                userType: this.userType,
                offerId: this.offerId,
                marginId: this.marginId,
                differences: this.offerMarginDifferences,
                candidateName: this.offer.candidateName
            }
        });
    }

    private _marginCreated(marginId: number) {
        this.openMargin(marginId);
        this.enableView.emit();
        this.addNoteEmit.emit({
            note: `Added Offer Margin [${marginId}]`,
            title: 'Added Offer Margin',
            excludeNotification: true
        });
    }

    goToJobId(id: number) {
        this.jobIdClick.emit(id);
    }

    goToContractRequest(contractRequestId: number, marginId: number) {
        this._window.open(`/#/recruiting/contract-request/${contractRequestId}?marginId=${marginId}`, '_blank');
    }

    openUnitFormUrl($event: MouseEvent, unitFormId: number) {
        $event.preventDefault();
        const url = this._accountManagersService.getUnitFormUrl(this.userId, unitFormId.toString());

        this.unitDescriptionSecureWindow.open(url, '_blank', true);
    }

    private _buildDateRangeStr(values: Set<Date[]>, nonConsecutive: boolean): string {
        let count = 0;
        let str = '';

        for (const element of values) {
            const array = nonConsecutive ? [] : this._mapDatesToFormat(Array.of(element)[0]);

            if (count === values.size - 1) {
                str += nonConsecutive
                    ? `${dayjs(element[0]).format(this.DATE_FORMAT_STRING)}`
                    : `${array[0]}-${array[array.length - 1]}`;
            } else {
                str += nonConsecutive
                    ? `${dayjs(element[0]).format(this.DATE_FORMAT_STRING)}, `
                    : `${array[0]}-${array[array.length - 1]}, `;
            }

            count++;
        }

        return str;
    }

    showCreateContractRequestButton() {
        return (
            !this.editMode &&
            this.marginId &&
            this.showImportMarginButton() &&
            this.offerData?.offerType === this.offerTypeEnum.Direct
        );
    }

    private _getDateNode(date: string): unknown {
        return this._renderer.selectRootElement(`td button[aria-label="${dayjs(date).format('MMMM D, YYYY')}"]`, true);
    }

    private _mapDatesToFormat(dates: Date[], format?: string): string[] {
        return dates.map((date) => dayjs(date).format(format && format !== '' ? format : this.DATE_FORMAT_STRING));
    }

    private _sortDates(dates: Date[]): Date[] {
        return dates.sort((first, second) => dayjs.utc(first).diff(second));
    }

    revertStatusId(originalStatusId: number) {
        this.detailsFormGroup.patchValue({ statusId: originalStatusId });
    }

    initContractLengthDaysAndWeeks(offer: Offer) {
        this.endDateTextWithoutWeeks = offer.endDateText?.split('(')[0];
        const obj = {
            changeType: 'initialLoad',
            startDate: offer.startDate,
            endDate: this.endDateTextWithoutWeeks,
            numOfShifts: offer.shiftsText.split('x')[0],
            facilityRate: offer.facilityRate,
            facilityId: offer.facilityId,
            candidateId: offer.candidateId
        };
        this.validateRTODatesforEditOrViewOffer();
        this.calculateContractDetails.emit(obj);
    }

    calculateContractDates(changeType: string): void {
        const obj = {
            changeType: changeType,
            startDate: this.detailsFormGroup.get('startDate').value,
            endDate: this.detailsFormGroup.get('endDate').value,
            contractLength: this.detailsFormGroup.get('contractLength').value,
            contractLengthDays: this.detailsFormGroup.get('contractLengthDays').value,
            numOfShifts: this.detailsFormGroup.get('shifts').value,
            facilityRate: this.detailsFormGroup.get('facilityRate').value,
            ayaPaidBonus: this.detailsFormGroup.get('ayaPaidBonus').value
        };
        this.calculateContractDetails.emit(obj);
        this.showDaysRequested();
    }

    createOfferMargin() {
        this.showCreateMarginDialog();
    }

    verballyAcceptOffer(): void {
        this.offerVerballyAccepted.emit(this.offerData.id);
    }

    disableSampleMargin(): boolean {
        return this.offerData?.sampleMarginId <= 0;
    }

    sampleMarginTooltip(): string {
        const toolTip =
            'Unable to create Margin. Please contact the Account Manager to add a sample margin on the job before proceeding.';

        if (this.offerData?.sampleMarginId <= 0) {
            return toolTip;
        }
        return '';
    }

    duplicateSampleMargin(): void {
        this.isLoading = true;
        this._marginCalculatorMarginService
            .duplicateSampleMargin(this.offerData.sampleMarginId, this.offerData.candidateId, this.offerId)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (marginId) => {
                    this.isLoading = false;
                    this.marginId = marginId;
                    this._marginCreated(marginId);
                },
                error: (errorMessage) => {
                    this.isLoading = false;
                    this._toasterService.toast(errorMessage, 7000);
                }
            });
    }

    get shouldDisplayNoteCategoryButtons() {
        return (
            this.isRecruiterSpecificNoteFlagEnabled &&
            this.userType === OffersUserType.AccountManager &&
            this.offer.offerType === OfferType.Direct
        );
    }

    get facilityCityAndState(): string {
        return this.offerData?.facilityCity && this.offerData?.facilityState
            ? `(${this.offerData.facilityCity}, ${this.offerData.facilityState})`
            : '';
    }

    private setOfferValuesOnLoad(): void {
        if (this.offerDetails && this.initialOfferValues === null) {
            this.initialOfferValues = {
                candidateRequestedTimeOff:
                    this.offerDetails?.requestedTimeOff?.map((date) => dayjs(date).toDate()) ?? [],
                jobDetails: {
                    jobId: this.offerDetails?.jobId,
                    profession: '',
                    specialty: this.expertise.find((e) => e.id === this.offerDetails?.expertiseId)?.name ?? null,
                    startDate: this.offerDetails?.startDate,
                    endDate: null,
                    length: this.displayedContractLengthWeeks,
                    shiftDetails: {
                        shifts: this.offerDetails?.shifts,
                        shiftTypeId: this.offerDetails?.shiftTypeId,
                        code: '',
                        name: '',
                        hours: this.offerDetails?.hours,
                        shiftH: this.offerDetails?.shiftH,
                        shiftM: this.offerDetails?.shiftM,
                        shiftToH: this.offerDetails?.shiftToH,
                        shiftToM: this.offerDetails?.shiftToM,
                        description: '',
                        hoursAlt: this.offerDetails?.hoursAlt,
                        shiftsAlt: this.offerDetails?.shiftsAlt,
                        shiftAltH: this.offerDetails?.shiftAltH,
                        shiftAltM: this.offerDetails?.shiftAltM,
                        shiftAltToH: this.offerDetails?.shiftAltToH,
                        shiftAltToM: this.offerDetails?.shiftAltToM
                    },
                    billRate: this.offerDetails?.facilityRate,
                    facilityBonus: null,
                    ayaPaidBonus: this.offerDetails?.ayaPaidBonus,
                    recruiterBonus: this.offerDetails?.recruiterBonus,
                    regularPayLow: null,
                    regularPayHigh: null,
                    targetMargin: this.offerDetails?.jobTargetMargin
                }
            };
        }

        this.snapshotData.set(this.initialOfferValues);
    }

    private clearEditModeValues(): void {
        this.initialOfferValues = null;
        this._offerDetails = null;
        this.displayedContractLengthWeeks = 0;
        this.snapshotData.set(null);
    }

    private _formatRtoDates(dates: Date[]): string {
        if (!dates?.length) {
            return '';
        }
        if (dates.length === 1) {
            return this._mapDatesToFormat(dates).join('');
        }

        let dateRangeStr = '';
        let nonDateRangeStr = '';
        const dateRangeSet = new Set<Date[]>();
        const nonDateRangeSet = new Set<Date[]>();

        let currentConsecutive: Date[] = [];
        const sortedDates = this._sortDates(dates);
        sortedDates.forEach((value) => {
            if (currentConsecutive.length === 0) {
                currentConsecutive.push(value);
                nonDateRangeSet.add(currentConsecutive);
                return;
            }

            const lastConsecutivePair = currentConsecutive[currentConsecutive.length - 1];

            if (dayjs(value).diff(lastConsecutivePair, 'days') === 1) {
                currentConsecutive.push(value);
            } else {
                currentConsecutive = [value];
            }

            if (currentConsecutive.length > 1) {
                dateRangeSet.add(currentConsecutive);

                for (const element of nonDateRangeSet) {
                    // Filter out dates from the non consecutive date range
                    if (dateRangeSet.has(element)) {
                        nonDateRangeSet.delete(element);
                    }
                }
            } else {
                nonDateRangeSet.add(currentConsecutive);
            }
        });

        dateRangeStr = this._buildDateRangeStr(dateRangeSet, false);
        nonDateRangeStr = this._buildDateRangeStr(nonDateRangeSet, true);

        if (nonDateRangeStr !== '' && dateRangeStr === '') {
            return `${nonDateRangeStr}`;
        } else if (dateRangeStr !== '' && nonDateRangeStr === '') {
            return `${dateRangeStr}`;
        } else {
            return `${nonDateRangeStr}, ${dateRangeStr}`;
        }
    }
}

export class MyErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: UntypedFormControl | null): boolean {
        return control && control.invalid;
    }
}
